import { Alert, Button, Input, message, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { UserHttp } from '@/services';
import { storage } from '@/utils/storage';
import { Flex } from '../common';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { createPasswordValidator } from '@/utils/passwordValidator';
import http, { defaultConfig, getUniversityUrl } from '@/services/http';



interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  requirements: string[];
  strength?: number;
}


export default function ForceChangePasswordModal() {
  const [t] = useTranslation(['common']);
  const [password, setPassword] = useState('');
  const [newPassword, setNewPasswowrd] = useState('');
  const [repeatNewPassword, setRepeatNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validatePassword, setValidatePassword] = useState<((password: string) => PasswordValidationResult) | null>(null);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult | null>(null);

  const token = storage.get('TOKEN', null);
  const user = token ? jwtDecode(token.toString()) as any : null;


  const passwordConfig = async() => {
    const baseURL = getUniversityUrl();
    return (await http.get(`/rest/users/password-configs?username=${user?.user_name}`, defaultConfig(baseURL))).data;
  }
  passwordConfig();

  // const { data: passwordConfig } = useQuery({
  //   queryKey: ['users/password-configs'],
  //   queryFn: async () => (await getPasswordConfig(user?.user_name)),
  //   enabled: !!user?.user_name
  // });

  useEffect(() => {
    if (passwordConfig[0]) {
      setValidatePassword(() => createPasswordValidator(passwordConfig[0]));
    }
  }, [passwordConfig]);

  const validateForm = useCallback(() => {
    if (!validatePassword) return true;

    const validation = validatePassword(newPassword || '');
    setPasswordValidation(validation);

    return validation.isValid;
  }, [validatePassword, newPassword]);

  const inputClassname = 'text-sm h-[40px]';

  const handleUpdate = useCallback(async () => {
    try {
      setError('');
      if (password.trim() === newPassword.trim()) {
        setError(t('common:errors.same-password'));
        return;
      }
      const isValid = validateForm();
      if (isValid) {
        setLoading(true);
        await UserHttp.changeMePassword({
          password,
          newPassword,
          repeatNewPassword,
        });
        storage.remove('TOKEN');
        storage.remove('REFRESH_TOKEN');
        message.success({ content: t('common:messages.success-password-change') });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        // Modal.destroyAll();
      }
    }
    catch (e) {
      setError(e?.response?.data?.messageFa ?? t('common:errors.connection-error-try-again'));
    }
    finally {
      setLoading(false);
    }
  }, [password, repeatNewPassword, newPassword, t]);



  const isDisabled =
    password.trim() === '' ||
    repeatNewPassword.trim() === '' ||
    newPassword.trim() === '' ||
    newPassword != repeatNewPassword;

  return (
    <Flex gap={12} direction="column">
      <Flex className="font-bold text-base mb-2">
        {t('common:labels.update')} {t('common:labels.password')}
      </Flex>
      <Alert
        className="mb-2"
        type="warning"
        message={(
          <div className="text-sm">
            {t('common:messages.expired-password')}
          </div>
        )}
      />
      <Input.Password
        size="middle"
        value={password}
        placeholder={t('common:labels.current-password')}
        className={inputClassname}
        onChange={(e) => setPassword(e.target.value)} />
      <Input.Password
        size="middle"
        value={newPassword}
        placeholder={t('common:labels.new-password')}
        className={inputClassname}
        onChange={(e) => setNewPasswowrd(e.target.value)} />
      {passwordValidation?.errors && passwordValidation.errors.length > 0 &&
        <div className="password-errors-list">
          {passwordValidation.errors.map((error, index) => (
            <div key={index} className="text-red-500 text-xs mt-1">
              {error}
            </div>
          ))}
        </div>
      }

      <Input.Password
        size="middle"
        value={repeatNewPassword}
        placeholder={t('common:labels.confirm-new-password')}
        className={inputClassname}
        onChange={(e) => setRepeatNewPassword(e.target.value)} />
      {error && <Typography.Text type="danger">{error}</Typography.Text>}
      <Button
        loading={loading}
        type="primary"
        className="ms-auto"
        disabled={isDisabled}
        onClick={handleUpdate}>
        {t('common:labels.update')}
      </Button>
    </Flex>
  );
}
